// ─────────────────────────────────────────────────────────────
// CommunityTab — top-level tab for browsing scripts shared by
// other Maps users. Differentiated from the workspace tabs by
// a hero strip, curated lanes (Trending / Recently shared /
// All scripts), author-forward cards with avatars, and a warm
// accent color. Preserves the rate-and-review flow that lives
// inside RateScriptDialog + ReviewsList.
//
// Promoted from a sub-tab of ScriptsTab on 2026-05-06 — all the
// rating + review behavior was preserved verbatim during the
// move; the new bits are the hero, the lane sections, the Fork
// action, and the avatar-led card layout.
// ─────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import { communityApi, imageApi, userScriptsApi } from "../../api/client";
import type {
  AuthUser,
  CommunityScript,
  ScriptRatingEntry,
  UserScript,
} from "../../api/client";
import { StarRating } from "./StarRating";
import { AuthorAvatar } from "./AuthorAvatar";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import { ScriptMetadataEditDialog } from "./ScriptMetadataEditDialog";

// ── Auth user helper ─────────────────────────────────────────

function getAuthUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem("auth_user");
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

// ── Community accent color ──────────────────────────────────
//
// One deliberate warm accent used for hero gradient, section
// headers, "your review" highlight, and the Fork button. We keep
// it as a hook-returned hex (not a hardcoded string sprinkled
// across the file) so it's easy to retune and so dark mode gets
// a slightly brighter shade for contrast against the dark surface.

function useCommunityAccent(): string {
  const theme = useTheme();
  return theme.palette.mode === "dark" ? "#fb923c" : "#c2410c"; // amber-500 / terracotta
}

// `AuthorAvatar` lives in ./AuthorAvatar.tsx (imported above).
// `formatRelativeTime` lives in src/utils/formatRelativeTime.ts (imported above).

// ── RateScriptDialog ─────────────────────────────────────────

const REVIEW_NOTE_MAX = 280;

interface RateScriptDialogProps {
  open: boolean;
  onClose: () => void;
  scriptId: number;
  scriptTitle: string;
  initialRating?: number;
  initialReviewNote?: string | null;
  isEdit: boolean;
  onSubmitted: (aggregate: { avg_rating: number; rating_count: number }) => void;
  onError: (message: string) => void;
  /** Optional Fork action shown in the dialog header. Hidden when undefined (e.g. own script). */
  onFork?: () => void;
  forkBusy?: boolean;
}

function RateScriptDialog({
  open,
  onClose,
  scriptId,
  scriptTitle,
  initialRating = 0,
  initialReviewNote = null,
  isEdit,
  onSubmitted,
  onError,
  onFork,
  forkBusy,
}: RateScriptDialogProps) {
  const [rating, setRating] = useState(initialRating);
  const [note, setNote] = useState(initialReviewNote ?? "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setRating(initialRating);
      setNote(initialReviewNote ?? "");
    }
  }, [open, initialRating, initialReviewNote]);

  const truncatedTitle = scriptTitle.length > 50 ? `${scriptTitle.slice(0, 50)}…` : scriptTitle;
  const dialogTitle = isEdit
    ? `Edit your review of "${truncatedTitle}"`
    : `Rate "${truncatedTitle}"`;
  const atMaxLength = note.length >= REVIEW_NOTE_MAX;
  const canSubmit = rating >= 1 && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    const trimmed = note.trim();
    const reviewNote = trimmed.length === 0 ? null : trimmed;
    try {
      const updated = await userScriptsApi.rate(scriptId, rating, reviewNote);
      onSubmitted(updated);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to submit rating.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          py: 1.5,
          px: 2,
        }}
      >
        <Typography variant="h3" component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {dialogTitle}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {onFork && (
            <Button
              size="small"
              variant="outlined"
              startIcon={forkBusy ? <CircularProgress size={14} /> : <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={onFork}
              disabled={forkBusy || submitting}
            >
              Fork
            </Button>
          )}
          <IconButton size="small" onClick={onClose} disabled={submitting} aria-label="Close">
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64 }}>
              Rating
            </Typography>
            <Box sx={{ "& > span": { gap: 0.5 } }}>
              <StarRating value={rating} interactive onChange={setRating} />
            </Box>
          </Box>

          <TextField
            label="Review note (optional)"
            placeholder="What worked well? Any gotchas?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={3}
            maxRows={4}
            fullWidth
            inputProps={{ maxLength: REVIEW_NOTE_MAX }}
            helperText={`${note.length} / ${REVIEW_NOTE_MAX}`}
            FormHelperTextProps={{
              sx: {
                textAlign: "right",
                color: atMaxLength ? "error.main" : "text.secondary",
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} disabled={submitting} variant="text">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="contained"
          disableElevation
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : isEdit ? "Update review" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── ReviewsList ─────────────────────────────────────────────

interface ReviewsListProps {
  ratingCount: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  ratings: ScriptRatingEntry[] | undefined;
  loading: boolean;
  loadError: string | null;
  onEditOwnReview: (entry: ScriptRatingEntry) => void;
  accent: string;
}

function ReviewsList({
  ratingCount,
  expanded,
  onToggleExpanded,
  ratings,
  loading,
  loadError,
  onEditOwnReview,
  accent,
}: ReviewsListProps) {
  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpanded();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onToggleExpanded();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse reviews" : "Expand reviews"}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          py: 0.5,
          color: "text.secondary",
          "&:hover": { color: "text.primary" },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "2px",
            borderRadius: "4px",
          },
        }}
      >
        {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          Reviews ({ratingCount})
        </Typography>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.75 }}
        >
          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading reviews…
              </Typography>
            </Box>
          )}

          {!loading && loadError && (
            <Typography variant="caption" color="error.main">
              {loadError}
            </Typography>
          )}

          {!loading && !loadError && ratings && ratings.length === 0 && (
            <Box sx={{ py: 1.5, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No written reviews yet. Be the first to leave one.
              </Typography>
            </Box>
          )}

          {!loading && !loadError && ratings && ratings.length > 0 && (
            <>
              {ratings.map((entry) => (
                <Paper
                  key={entry.id}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderRadius: "6px",
                    borderLeft: entry.is_own_rating ? `3px solid ${accent}` : undefined,
                    bgcolor: entry.is_own_rating ? alpha(accent, 0.08) : "background.paper",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    <StarRating value={entry.rating} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {entry.author_name}
                    </Typography>
                    {entry.is_own_rating && (
                      <Chip
                        label="your review"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          borderRadius: "3px",
                          bgcolor: accent,
                          color: "#fff",
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    )}
                    <Typography variant="caption" color="text.disabled">
                      {formatRelativeTime(entry.created_at)}
                    </Typography>
                    {entry.is_own_rating && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => onEditOwnReview(entry)}
                        aria-label="Edit your review"
                        sx={{
                          ml: "auto",
                          minWidth: 0,
                          py: 0,
                          px: 0.75,
                          fontSize: "0.6875rem",
                          textTransform: "none",
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>
                  {entry.review_note && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                    >
                      {entry.review_note}
                    </Typography>
                  )}
                </Paper>
              ))}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Section header ──────────────────────────────────────────

interface SectionHeaderProps {
  label: string;
  caption?: string;
  accent: string;
  trailing?: React.ReactNode;
}

function SectionHeader({ label, caption, accent, trailing }: SectionHeaderProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          width: 4,
          height: 18,
          borderRadius: "2px",
          bgcolor: accent,
          flexShrink: 0,
        }}
      />
      <Typography variant="overline" sx={{ color: accent, fontWeight: 700 }}>
        {label}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          · {caption}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      {trailing}
    </Box>
  );
}

// ── Community card banner ───────────────────────────────────
//
// Visual anchor at the top of every community card. Two states:
//   1. Script has `default_image_id` → fetch the 400px thumbnail
//      (blob URL via imageApi.getThumbnailUrl) and render it as a
//      cover-fit banner. Falls back to the placeholder treatment
//      on fetch error (e.g., the paired image was deleted).
//   2. No paired image → quiet surface-tier panel with a centered
//      ImageOutlined icon. Same height as the thumbnail case so
//      cards line up in the grid regardless of pairing status.
//
// The banner is purely decorative — the whole card is the click
// target (handled by the parent Card). We stop nothing here.
//
// The blob URL is revoked on unmount / id change so we don't leak
// object URLs as the user scrolls the community feed.

const BANNER_HEIGHT = 140;

interface CommunityCardBannerProps {
  imageId: number | null;
  /**
   * Card border radius (resolved px). The banner's top corners
   * must match the card's outer radius for a flush look since
   * the banner escapes the card's inner padding via negative
   * margins.
   */
  topRadiusPx: number;
}

function CommunityCardBanner({ imageId, topRadiusPx }: CommunityCardBannerProps) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    setThumbUrl(null);
    setLoadFailed(false);
    if (imageId == null) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    imageApi
      .getThumbnailUrl(imageId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setThumbUrl(url);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [imageId]);

  const showPlaceholder = imageId == null || loadFailed;

  return (
    <Box
      sx={{
        height: BANNER_HEIGHT,
        // Escape the Card's px/pt: 2 so the banner is flush at the top.
        mx: -2,
        mt: -2,
        mb: 1.5,
        borderTopLeftRadius: `${topRadiusPx}px`,
        borderTopRightRadius: `${topRadiusPx}px`,
        overflow: "hidden",
        bgcolor: "surfaceContainerLow",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      {showPlaceholder ? (
        <ImageOutlinedIcon
          sx={{ fontSize: 36, color: "text.disabled" }}
          aria-hidden
        />
      ) : thumbUrl ? (
        <Box
          component="img"
          src={thumbUrl}
          alt=""
          loading="lazy"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
          onError={() => setLoadFailed(true)}
        />
      ) : (
        // Loading state — quiet surface, no spinner so the rest of
        // the card reads cleanly while the thumbnail streams in.
        <Box sx={{ width: "100%", height: "100%" }} aria-hidden />
      )}
    </Box>
  );
}

// ── Community card ──────────────────────────────────────────

interface CommunityCardProps {
  script: CommunityScript;
  ratingData: { avg_rating: number; rating_count: number };
  isOwnScript: boolean;
  isTopRated?: boolean;
  variant: "feature" | "grid";
  onLoad: () => void;
  onRate: (presetStar?: number) => void;
  onFork: () => void;
  forkBusy: boolean;
  reviewsExpanded: boolean;
  onToggleReviews: () => void;
  ratings: ScriptRatingEntry[] | undefined;
  ratingsLoading: boolean;
  ratingsError: string | null;
  onEditOwnReview: () => void;
  /** Author-only edit affordance — opens the metadata edit dialog. */
  onEditScript?: () => void;
  accent: string;
}

function CommunityCard({
  script,
  ratingData,
  isOwnScript,
  isTopRated,
  variant,
  onLoad,
  onRate,
  onFork,
  forkBusy,
  reviewsExpanded,
  onToggleReviews,
  ratings,
  ratingsLoading,
  ratingsError,
  onEditOwnReview,
  onEditScript,
  accent,
}: CommunityCardProps) {
  const isFeature = variant === "feature";
  return (
    <Card
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={onLoad}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onLoad();
        }
      }}
      aria-label={`Load ${script.title}`}
      sx={{
        bgcolor: "surfaceContainerHigh",
        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.10)"
            : "rgba(0,0,0,0.10)",
        borderRadius: 2,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        p: 2,
        minHeight: isFeature ? 220 : undefined,
        transition: "border-color 150ms, background-color 150ms",
        position: "relative",
        "&:hover": {
          bgcolor: "surfaceContainerHighest",
          borderColor: "primary.main",
          "& .script-card-action": { opacity: 1 },
        },
        "&:focus-visible": {
          outline: "3px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
        },
      }}
    >
      {isTopRated && (
        <Tooltip title="Top rated this week">
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: accent,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: 1,
              // Stay on top of the banner thumbnail (banner is a later
              // sibling in the DOM and would otherwise render above).
              zIndex: 1,
            }}
          >
            <EmojiEventsOutlinedIcon sx={{ fontSize: 14 }} />
          </Box>
        </Tooltip>
      )}

      {/* Visual banner — paired image thumbnail or quiet placeholder.
          Sits flush at the top of the card by escaping the Card's
          inner padding via negative margins. Card outer radius is
          `borderRadius: 2` → theme.shape.borderRadius (4) * 2 = 8px. */}
      <CommunityCardBanner
        imageId={script.default_image_id}
        topRadiusPx={8}
      />

      {/* Author row — leads the card */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <AuthorAvatar name={script.author_name} size={28} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", lineHeight: 1.2 }} noWrap>
            {script.author_name}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.6875rem" }}>
            shared {formatRelativeTime(script.created_at)}
          </Typography>
        </Box>
      </Box>

      {/* Title */}
      <Typography
        variant={isFeature ? "subtitle1" : "subtitle1"}
        sx={{
          fontWeight: 600,
          lineHeight: 1.3,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          mb: 0.5,
        }}
      >
        {script.title}
      </Typography>

      {/* Description */}
      {script.description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: isFeature ? 3 : 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            mb: 1.25,
          }}
        >
          {script.description}
        </Typography>
      )}

      <Box sx={{ flex: 1 }} />

      <Divider sx={{ my: 1 }} />

      {/* Bottom row — rating + reviews chip + actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Tooltip title={isOwnScript ? "You can't rate your own script" : ""} disableHoverListener={!isOwnScript}>
            <Box
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: isOwnScript ? "default" : "pointer" }}
            >
              <StarRating
                value={ratingData.avg_rating}
                interactive={!isOwnScript}
                onChange={isOwnScript ? undefined : (val) => onRate(val)}
              />
              <Typography variant="caption" color="text.secondary">
                ({ratingData.rating_count})
              </Typography>
            </Box>
          </Tooltip>

          {/* Fork count — quiet label, only shown once at least one fork exists. */}
          {script.fork_count > 0 && (
            <Tooltip title={`Forked ${script.fork_count} ${script.fork_count === 1 ? "time" : "times"}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: "text.secondary" }}>
                <CallSplitOutlinedIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption" color="text.secondary">
                  {script.fork_count} {script.fork_count === 1 ? "fork" : "forks"}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        <Box
          className="script-card-action"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: 0.8, transition: "opacity 200ms" }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {isOwnScript && onEditScript && (
            <Tooltip title="Edit title, description, or paired image">
              <IconButton
                size="small"
                onClick={onEditScript}
                aria-label={`Edit ${script.title}`}
                sx={{ p: 0.5 }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {!isOwnScript && (
            <Tooltip title="Save a private copy to My Scripts">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={forkBusy ? <CircularProgress size={12} /> : <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />}
                  onClick={onFork}
                  disabled={forkBusy}
                  sx={{ minHeight: 24, py: 0.25, px: 1, fontSize: "0.6875rem" }}
                >
                  Fork
                </Button>
              </span>
            </Tooltip>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: "primary.main" }}>
            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "0.6875rem" }}>
              Load
            </Typography>
            <ArrowForwardOutlinedIcon sx={{ fontSize: 14 }} />
          </Box>
        </Box>
      </Box>

      <ReviewsList
        ratingCount={ratingData.rating_count}
        expanded={reviewsExpanded}
        onToggleExpanded={onToggleReviews}
        ratings={ratings}
        loading={ratingsLoading}
        loadError={ratingsError}
        onEditOwnReview={onEditOwnReview}
        accent={accent}
      />
    </Card>
  );
}

// ── Hero strip ──────────────────────────────────────────────

interface HeroStripProps {
  accent: string;
  scriptCount: number | null;
  contributorCount: number | null;
  reviewCount: number | null;
}

function HeroStrip({ accent, scriptCount, contributorCount, reviewCount }: HeroStripProps) {
  const stats = [
    { value: scriptCount, label: scriptCount === 1 ? "script" : "scripts" },
    { value: contributorCount, label: contributorCount === 1 ? "contributor" : "contributors" },
    { value: reviewCount, label: reviewCount === 1 ? "review" : "reviews" },
  ];
  return (
    <Box
      sx={{
        px: 3,
        py: 2.5,
        background: `linear-gradient(135deg, ${alpha(accent, 0.14)}, ${alpha(accent, 0.03)})`,
        borderBottom: "1px solid",
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GroupsOutlinedIcon sx={{ fontSize: 22, color: accent }} />
          <Typography variant="h1" component="h1" sx={{ fontSize: "1.375rem" }}>
            Community Scripts
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
          Scripts shared by other Maps users — rate, review, and fork what works for your workflow.
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
        {stats.map((stat, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
            {stat.value === null ? (
              <Skeleton width={28} height={20} />
            ) : (
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: accent }}>
                {stat.value}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {stat.label}
            </Typography>
            {i < stats.length - 1 && (
              <Typography variant="caption" color="text.disabled" sx={{ ml: 0.75 }}>
                ·
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Main component ──────────────────────────────────────────

export type CommunitySortMode = "popularity" | "newest" | "rating";

interface CommunityTabProps {
  /**
   * Legacy generic load callback. Retained for any non-community-sourced
   * load path that might be added here in the future. Community-card
   * loads use `onLoadCommunityScript` so the backend Marie-intro endpoint
   * fires with the full script payload (title + author + description + code).
   */
  onLoad: (code: string, defaultImageId?: number | null, savedScriptId?: number | null, scriptTitle?: string) => void;
  /**
   * Called when the user loads a community-sourced script. ScriptHelperApp
   * routes this through the editor + fires the Marie introduce-script
   * endpoint so the python tab's AI panel gets a Marie-voiced intro
   * crediting the author.
   */
  onLoadCommunityScript: (script: CommunityScript) => void;
  /** Called when the user wants to jump to Scripts → My Scripts subtab (after a successful Fork). */
  onNavigateToMyScripts: () => void;
}

export function CommunityTab({ onLoad: _onLoad, onLoadCommunityScript, onNavigateToMyScripts }: CommunityTabProps) {
  // `onLoad` is retained on the prop surface for symmetry with the other
  // load-script paths and to keep the door open for future non-community
  // loads inside this tab. Today every load on this tab is community-sourced.
  void _onLoad;
  const accent = useCommunityAccent();
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState<CommunitySortMode>("popularity");
  const [scripts, setScripts] = useState<CommunityScript[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [ratingOverrides, setRatingOverrides] = useState<Record<number, { avg_rating: number; rating_count: number }>>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
    actionLabel?: string;
    onAction?: () => void;
  }>({ open: false, message: "", severity: "success" });
  const [expandedReviews, setExpandedReviews] = useState<Record<number, boolean>>({});
  const [ratingsCache, setRatingsCache] = useState<Record<number, ScriptRatingEntry[]>>({});
  const [ratingsLoading, setRatingsLoading] = useState<Record<number, boolean>>({});
  const [ratingsError, setRatingsError] = useState<Record<number, string | null>>({});
  const [forkBusy, setForkBusy] = useState<Record<number, boolean>>({});
  const [rateDialog, setRateDialog] = useState<{
    open: boolean;
    script: CommunityScript | null;
    initialRating: number;
    initialReviewNote: string | null;
    isEdit: boolean;
  }>({ open: false, script: null, initialRating: 0, initialReviewNote: null, isEdit: false });
  // Metadata edit dialog (Greg, 2026-05-14) — author-only affordance on
  // the user's own community card. Holds the script being edited; null
  // when the dialog is closed.
  const [editDialogScript, setEditDialogScript] = useState<CommunityScript | null>(null);

  const authUser = getAuthUser();

  function loadScripts() {
    setLoading(true);
    setFetchError(null);
    communityApi
      .list()
      .then((data) => setScripts(data))
      .catch((err) => setFetchError(err instanceof Error ? err.message : "Failed to load community scripts."))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadScripts();
  }, []);

  function fetchRatings(scriptId: number) {
    setRatingsLoading((prev) => ({ ...prev, [scriptId]: true }));
    setRatingsError((prev) => ({ ...prev, [scriptId]: null }));
    communityApi
      .listRatings(scriptId)
      .then((data) => {
        setRatingsCache((prev) => ({ ...prev, [scriptId]: data }));
      })
      .catch((err) => {
        setRatingsError((prev) => ({
          ...prev,
          [scriptId]: err instanceof Error ? err.message : "Failed to load reviews.",
        }));
      })
      .finally(() => {
        setRatingsLoading((prev) => ({ ...prev, [scriptId]: false }));
      });
  }

  function handleToggleReviews(scriptId: number) {
    setExpandedReviews((prev) => {
      const next = { ...prev, [scriptId]: !prev[scriptId] };
      if (next[scriptId] && ratingsCache[scriptId] === undefined && !ratingsLoading[scriptId]) {
        fetchRatings(scriptId);
      }
      return next;
    });
  }

  function findOwnRating(scriptId: number): ScriptRatingEntry | undefined {
    return ratingsCache[scriptId]?.find((r) => r.is_own_rating);
  }

  function openRateDialog(script: CommunityScript, presetStar?: number) {
    const own = findOwnRating(script.id);
    setRateDialog({
      open: true,
      script,
      initialRating: own?.rating ?? presetStar ?? 0,
      initialReviewNote: own?.review_note ?? null,
      isEdit: own !== undefined,
    });
  }

  function closeRateDialog() {
    setRateDialog((prev) => ({ ...prev, open: false }));
  }

  function handleRateSubmitted(scriptId: number, aggregate: { avg_rating: number; rating_count: number }) {
    setRatingOverrides((prev) => ({ ...prev, [scriptId]: aggregate }));
    setRatingsCache((prev) => {
      const next = { ...prev };
      delete next[scriptId];
      return next;
    });
    if (expandedReviews[scriptId]) {
      fetchRatings(scriptId);
    }
  }

  async function handleFork(script: CommunityScript) {
    if (forkBusy[script.id]) return;
    setForkBusy((prev) => ({ ...prev, [script.id]: true }));
    try {
      // Server-side fork (Sam, 2026-05-15): the backend builds the copy
      // (title, clean v1, lineage) and increments fork_count on the
      // original. The frontend just hands it the source script id.
      await userScriptsApi.fork(script.id);
      // The original's fork_count is now +1 server-side — reflect that
      // on the card the user just forked without a full reload.
      setScripts((prev) =>
        prev.map((s) =>
          s.id === script.id ? { ...s, fork_count: s.fork_count + 1 } : s,
        ),
      );
      setSnackbar({
        open: true,
        message: `Forked "${script.title}" to My Scripts.`,
        severity: "success",
        actionLabel: "Go to My Scripts",
        onAction: () => {
          setSnackbar((s) => ({ ...s, open: false }));
          onNavigateToMyScripts();
        },
      });
      // If the rate dialog was open for the forked script, close it
      if (rateDialog.open && rateDialog.script?.id === script.id) {
        closeRateDialog();
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to fork script.",
        severity: "error",
      });
    } finally {
      setForkBusy((prev) => ({ ...prev, [script.id]: false }));
    }
  }

  // ── Derive lanes + filtered/sorted "All" list ───────────────

  function isOwn(script: CommunityScript): boolean {
    return authUser?.full_name === script.author_name;
  }

  function effectiveRating(script: CommunityScript): { avg_rating: number; rating_count: number } {
    return ratingOverrides[script.id] ?? { avg_rating: script.avg_rating, rating_count: script.rating_count };
  }

  // Trending = top 3 by rating then count. Recently shared = top 3 newest.
  // "All scripts" excludes the items already shown in either lane (so users
  // don't see the same card twice on the page).
  const { trending, recent, allRest, contributorCount, totalReviews } = useMemo(() => {
    const trendingPool = [...scripts].sort((a, b) => {
      const ra = effectiveRating(a);
      const rb = effectiveRating(b);
      if (rb.avg_rating !== ra.avg_rating) return rb.avg_rating - ra.avg_rating;
      return rb.rating_count - ra.rating_count;
    });
    // Don't surface "Trending" entries with zero ratings — they're not actually trending.
    const trending = trendingPool.filter((s) => effectiveRating(s).rating_count > 0).slice(0, 3);
    const recent = [...scripts]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 3);
    const featuredIds = new Set([...trending, ...recent].map((s) => s.id));
    const allRest = scripts.filter((s) => !featuredIds.has(s.id));
    const contributors = new Set(scripts.map((s) => s.author_name));
    const totalReviews = scripts.reduce((sum, s) => sum + effectiveRating(s).rating_count, 0);
    return {
      trending,
      recent,
      allRest,
      contributorCount: contributors.size,
      totalReviews,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scripts, ratingOverrides]);

  // When the user is actively searching, hide the curated Trending and
  // Recently shared lanes — they're curated rules (top-rated / newest 3)
  // and don't respect the search query, which made the page feel like
  // search was broken. With an active search, only the filtered All
  // Scripts grid renders. Whitespace-only search is treated as empty.
  const hasActiveSearch = search.trim().length > 0;

  const filteredAndSorted = useMemo(() => {
    const q = search.toLowerCase().trim();
    let list = q
      ? allRest.filter(
          (s) =>
            s.title.toLowerCase().includes(q) ||
            s.author_name.toLowerCase().includes(q) ||
            (s.description ?? "").toLowerCase().includes(q),
        )
      : [...allRest];
    if (sortMode === "newest") {
      list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } else if (sortMode === "rating") {
      list.sort((a, b) => effectiveRating(b).avg_rating - effectiveRating(a).avg_rating);
    } else {
      // popularity: rating_count desc, then avg_rating desc
      list.sort((a, b) => {
        const ra = effectiveRating(a);
        const rb = effectiveRating(b);
        if (rb.rating_count !== ra.rating_count) return rb.rating_count - ra.rating_count;
        return rb.avg_rating - ra.avg_rating;
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRest, search, sortMode, ratingOverrides]);

  // ── Render helpers ──────────────────────────────────────────

  function renderCard(script: CommunityScript, variant: "feature" | "grid", isTopRated = false) {
    const ownScript = isOwn(script);
    return (
      <CommunityCard
        key={script.id}
        script={script}
        ratingData={effectiveRating(script)}
        isOwnScript={ownScript}
        isTopRated={isTopRated}
        variant={variant}
        onLoad={() => onLoadCommunityScript(script)}
        onRate={(presetStar) => openRateDialog(script, presetStar)}
        onFork={() => handleFork(script)}
        forkBusy={!!forkBusy[script.id]}
        reviewsExpanded={!!expandedReviews[script.id]}
        onToggleReviews={() => handleToggleReviews(script.id)}
        ratings={ratingsCache[script.id]}
        ratingsLoading={!!ratingsLoading[script.id]}
        ratingsError={ratingsError[script.id] ?? null}
        onEditOwnReview={() => openRateDialog(script)}
        onEditScript={ownScript ? () => setEditDialogScript(script) : undefined}
        accent={accent}
      />
    );
  }

  // Merge the updated row back into local state on a successful metadata
  // edit, then re-fetch the community list so the lanes reflect the new
  // ordering (rare edge-case: a title change can shift sort position).
  function handleEditScriptSuccess(updated: UserScript) {
    setScripts((prev) =>
      prev.map((s) =>
        s.id === updated.id
          ? {
              ...s,
              title: updated.title,
              description: updated.description,
              default_image_id: updated.default_image_id,
              updated_at: updated.updated_at,
            }
          : s,
      ),
    );
    setEditDialogScript(null);
    setSnackbar({
      open: true,
      message: `"${updated.title}" updated.`,
      severity: "success",
    });
    // Best-effort refresh — keeps the lanes coherent if backend ordering
    // shifted. Failure is silent; local state is already correct.
    loadScripts();
  }

  // ── Loading state ───────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <HeroStrip accent={accent} scriptCount={null} contributorCount={null} reviewCount={null} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, justifyContent: "center" }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Loading community scripts...</Typography>
        </Box>
      </Box>
    );
  }

  // ── Error state ─────────────────────────────────────────────
  if (fetchError) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <HeroStrip accent={accent} scriptCount={0} contributorCount={0} reviewCount={0} />
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 1.5, p: 3 }}>
          <Typography variant="body2" color="error.main" sx={{ textAlign: "center" }}>{fetchError}</Typography>
          <Button size="small" variant="outlined" onClick={loadScripts}>Retry</Button>
        </Box>
      </Box>
    );
  }

  const handleSortChange = (e: SelectChangeEvent<CommunitySortMode>) => {
    setSortMode(e.target.value as CommunitySortMode);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          // Subtle radial tint on the tab background — keeps the surface
          // distinct from the workspace tabs without shouting.
          background: (theme) =>
            theme.palette.mode === "dark"
              ? `radial-gradient(ellipse at top right, ${alpha(accent, 0.05)}, transparent 60%)`
              : `radial-gradient(ellipse at top right, ${alpha(accent, 0.04)}, transparent 60%)`,
        }}
      >
        <HeroStrip
          accent={accent}
          scriptCount={scripts.length}
          contributorCount={contributorCount}
          reviewCount={totalReviews}
        />

        {/* ── Search + sort row ─────────────────────────────────
            Lifted out of the All Scripts section header on
            2026-05-15 (Greg, bug report: the search box was
            buried at the bottom of the page and felt invisible).
            Lives directly under the hero strip so it's the first
            thing the user sees on the tab.

            Behavior is unchanged from the previous in-header
            placement: search + sort filter ONLY the All Scripts
            grid below — the Trending and Recently shared lanes
            keep their own curated rules (top-rated / newest 3).
            Match the hero's `px: 3` so the row aligns with the
            hero's content width; `py: 1.5` keeps it visually
            attached to the hero rather than floating. */}
        {scripts.length > 0 && (
          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <TextField
              placeholder="Search scripts, authors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Select
              value={sortMode}
              onChange={handleSortChange}
              size="small"
              sx={{ minWidth: 140 }}
              aria-label="Sort all scripts"
            >
              <MenuItem value="popularity">Popularity</MenuItem>
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="rating">Highest rated</MenuItem>
            </Select>
          </Box>
        )}

        {/* Empty state — no scripts at all */}
        {scripts.length === 0 ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
            <Box sx={{ textAlign: "center", maxWidth: 360 }}>
              <GroupsOutlinedIcon sx={{ fontSize: 64, color: accent, mb: 1.5 }} />
              <Typography variant="h3" sx={{ mb: 1 }}>
                No community scripts yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Be the first to share one — head to <strong>Scripts → My Scripts</strong> and toggle the publish icon on a saved script.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2.5 }}>
            {/* ── Curated lanes ──────────────────────────────────
                Trending + Recently shared render together or not at
                all. They're hidden whenever the search field has any
                non-whitespace text (Greg, 2026-05-15): the lanes use
                curated rules (top-rated / newest 3) that don't respect
                the search query, so showing them alongside a filtered
                grid made search feel broken. Empty/whitespace search
                → both lanes render as before. */}
            {!hasActiveSearch && (
              <>
                {/* ── Trending lane ─────────────────────────────── */}
                {trending.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <SectionHeader label="Trending" caption="Top-rated this week" accent={accent} />
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                        gap: 2,
                      }}
                    >
                      {trending.map((s, i) => renderCard(s, "feature", i === 0))}
                    </Box>
                  </Box>
                )}

                {/* ── Recently shared lane ───────────────────────── */}
                {recent.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <SectionHeader label="Recently shared" caption="Fresh from the community" accent={accent} />
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                        gap: 2,
                      }}
                    >
                      {recent.map((s) => renderCard(s, "feature"))}
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/* ── All scripts grid ─────────────────────────────── */}
            {/* Search + sort UI moved to the top of the tab on 2026-05-15
                (Greg's bug report — it was invisible buried down here).
                The state and filter logic are unchanged; this section
                just consumes the same `search` + `sortMode` it always did. */}
            <Box>
              <SectionHeader
                label="All scripts"
                caption={`${allRest.length} more`}
                accent={accent}
              />
              {filteredAndSorted.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: "center",
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    border: "1px dashed",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {search ? `No scripts match "${search}"` : "No additional scripts beyond the lanes above."}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                    gap: 2,
                  }}
                >
                  {filteredAndSorted.map((s) => renderCard(s, "grid"))}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {rateDialog.script && (
        <RateScriptDialog
          open={rateDialog.open}
          onClose={closeRateDialog}
          scriptId={rateDialog.script.id}
          scriptTitle={rateDialog.script.title}
          initialRating={rateDialog.initialRating}
          initialReviewNote={rateDialog.initialReviewNote}
          isEdit={rateDialog.isEdit}
          onSubmitted={(aggregate) => {
            if (rateDialog.script) {
              handleRateSubmitted(rateDialog.script.id, aggregate);
            }
          }}
          onError={(message) => setSnackbar({ open: true, message, severity: "error" })}
          onFork={
            rateDialog.script && !isOwn(rateDialog.script)
              ? () => rateDialog.script && handleFork(rateDialog.script)
              : undefined
          }
          forkBusy={rateDialog.script ? !!forkBusy[rateDialog.script.id] : false}
        />
      )}

      <ScriptMetadataEditDialog
        open={editDialogScript !== null}
        script={editDialogScript}
        onClose={() => setEditDialogScript(null)}
        onSuccess={handleEditScriptSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
          action={
            snackbar.actionLabel && snackbar.onAction ? (
              <Button color="inherit" size="small" onClick={snackbar.onAction}>
                {snackbar.actionLabel}
              </Button>
            ) : undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
